import type { ExamImage } from "$global-providers/rxdb-provider/collections/epocus-exam-worksheets";
import {
  type ImagingInstance,
  RXDB_IMAGING_INSTANCE_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/imaging-instance";
import { gql, useApolloClient } from "@apollo/client";
import { ImagingAssetKind } from "elephant-web/gql";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useRxData } from "rxdb-hooks";
import { readCachedBlob } from "../cine/cine-asset-store";
import { type CineMetadata, toCineMetadata } from "../cine/cine-metadata";
import { fetchImagingAssetUrl } from "./imaging-asset-url";

gql`
  query EpocusGenerateImagingAssetUrl($input: ImagingAssetUrlInput!) {
    generateImagingAssetUrl(input: $input) {
      url
    }
  }
`;

export type HydratedExamImage = ExamImage & {
  previewUrl?: string;
  loading: boolean;
  cine: CineMetadata | null;
};

export const useEpocusExamImages = (
  images: ExamImage[],
): HydratedExamImage[] => {
  const apolloClient = useApolloClient();

  const [hydratedImages, setHydratedImages] = useState<HydratedExamImage[]>(
    () =>
      images.map((img) => ({
        ...img,
        previewUrl: undefined,
        loading: true,
        cine: null,
      })),
  );

  const getPreviewUrl = useCallback(
    async (
      imagingInstanceId: string,
      trackObjectUrl: (url: string) => void,
    ): Promise<string | null> => {
      try {
        const cached = await readCachedBlob(
          imagingInstanceId,
          ImagingAssetKind.Preview,
        );
        if (cached) {
          const objectUrl = URL.createObjectURL(cached);
          trackObjectUrl(objectUrl);
          return objectUrl;
        }
        return await fetchImagingAssetUrl(
          apolloClient,
          imagingInstanceId,
          ImagingAssetKind.Preview,
        );
      } catch (error) {
        console.error(
          `Failed to generate preview URL for ${imagingInstanceId}:`,
          error,
        );
        return null;
      }
    },
    [apolloClient],
  );

  useEffect(() => {
    let isCancelled = false;
    const objectUrls: string[] = [];

    setHydratedImages(
      images.map((img) => ({
        ...img,
        previewUrl: undefined,
        loading: true,
        cine: null,
      })),
    );

    const process = async () => {
      await Promise.all(
        images.map(async (image) => {
          const previewUrl = await getPreviewUrl(image.id, (url) =>
            objectUrls.push(url),
          );
          if (!isCancelled) {
            setHydratedImages((prev) =>
              prev.map((img) =>
                img.id === image.id
                  ? {
                      ...img,
                      previewUrl: previewUrl ?? undefined,
                      loading: false,
                    }
                  : img,
              ),
            );
          }
        }),
      );
    };

    process();

    return () => {
      isCancelled = true;
      for (const url of objectUrls) URL.revokeObjectURL(url);
    };
    // biome-ignore lint/correctness/useExhaustiveDependencies: images is the full dep; RxDB useRxData returns a stable reference when data hasn't changed
  }, [images, getPreviewUrl]);

  const imageIds = useMemo(() => images.map((img) => img.id), [images]);

  // Cine status flips PENDING → READY server-side after the exam document was
  // written, so the live imaging-instance row is the only honest source for it.
  const { result: instances } = useRxData<ImagingInstance>(
    RXDB_IMAGING_INSTANCE_COLLECTION_NAME,
    (collection) =>
      imageIds.length > 0
        ? collection.find({ selector: { id: { $in: imageIds } } })
        : undefined,
  );

  const cineByInstanceId = useMemo(() => {
    const byId = new Map<string, CineMetadata>();
    for (const instance of instances ?? []) {
      const metadata = toCineMetadata(instance);
      if (metadata) byId.set(instance.id, metadata);
    }
    return byId;
  }, [instances]);

  return useMemo(
    () =>
      hydratedImages.map((img) => ({
        ...img,
        cine: cineByInstanceId.get(img.id) ?? null,
      })),
    [hydratedImages, cineByInstanceId],
  );
};
